import { defineSemanticTokens } from '@chakra-ui/react';

/**
 * Brand semantic tokens for #4f15ac (Deep Violet)
 *
 * These follow the exact same slot structure Chakra UI uses for every
 * built-in color palette (gray, blue, red, green …):
 *
 *   brand.contrast     – text/icon colour on a solid brand background
 *   brand.fg           – foreground text in a branded context
 *   brand.subtle       – lightest tinted background
 *   brand.muted        – slightly stronger tinted background
 *   brand.emphasized   – border / divider tint
 *   brand.solid        – the primary "filled" colour
 *   brand.focusRing    – focus-ring colour
 *   brand.border       – border colour
 *
 * Usage in recipes / components:
 *   colorPalette="brand"
 */
export const brandSemanticColors = defineSemanticTokens.colors({
  brand: {
    /**
     * Text / icon rendered ON TOP of a solid brand background.
     * White works in both modes because brand.600 and brand.500 are both dark.
     */
    contrast: {
      value: {
        _light: 'white',
        _dark: 'white',
      },
    },

    /**
     * Brand-coloured text / icons rendered on a neutral background.
     * In light mode: darker shade so it reads at WCAG AA against white.
     * In dark mode : lighter shade so it reads against dark surfaces.
     */
    fg: {
      value: {
        _light: '{colors.brand.700}',
        _dark: '{colors.brand.300}',
      },
    },

    /**
     * The very lightest brand-tinted surface – used for hover states,
     * badge backgrounds, tag fills etc.
     */
    subtle: {
      value: {
        _light: '{colors.brand.100}',
        _dark: '{colors.brand.900}',
      },
    },

    /**
     * A step stronger than subtle – pressed states, selected rows, etc.
     */
    muted: {
      value: {
        _light: '{colors.brand.200}',
        _dark: '{colors.brand.800}',
      },
    },

    /**
     * Used for borders, dividers, and visual separators.
     */
    emphasized: {
      value: {
        _light: '{colors.brand.300}',
        _dark: '{colors.brand.700}',
      },
    },

    /**
     * The primary "filled" brand colour – Button solid, Badge solid, etc.
     * Light mode uses brand.600 (#4f15ac) – the exact brand hex.
     * Dark mode steps up to brand.500 for better contrast on dark surfaces.
     */
    solid: {
      value: {
        _light: '{colors.brand.600}',
        _dark: '{colors.brand.500}',
      },
    },

    /**
     * Focus ring colour.  Slightly lighter than solid so the ring
     * is visible both inside and outside the component.
     */
    focusRing: {
      value: {
        _light: '{colors.brand.500}',
        _dark: '{colors.brand.400}',
      },
    },

    /**
     * Border colour used on outlined / surface components.
     */
    border: {
      value: {
        _light: '{colors.brand.500}',
        _dark: '{colors.brand.400}',
      },
    },
  },
});
